import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowRightIcon, CheckCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { H1, H2, H3, Body, BodyLarge } from '../components/ui/Typography';

const ranks = [
  {
    title: "Apprentice",
    reward: "Unpaid",
    level: "Starting Level",
    description: "Learn and prove your content creation skills",
    requirements: "Must reach 75+ points to advance"
  },
  {
    title: "Property Scout",
    reward: "$100",
    level: "Entry Level",
    description: "Start your journey in real estate content creation",
    requirements: "75+ points monthly"
  },
  {
    title: "Home Advisor",
    reward: "$150",
    level: "Intermediate",
    description: "Share deeper insights about property investment",
    requirements: "150+ points & top 10 performance"
  },
  {
    title: "Property Manager",
    reward: "$250",
    level: "Advanced",
    description: "Create comprehensive real estate content",
    requirements: "150+ points & exceptional performance"
  },
  {
    title: "Real Estate Developer",
    reward: "$500",
    level: "Elite",
    description: "Lead the community with expert content",
    requirements: "150+ points & leadership qualities"
  }
];

const taskPoints = {
  writing: [
    { task: "Medium Article", points: 20, frequency: "1 per week" },
    { task: "X/Twitter Thread", points: 20, frequency: "1 per week" },
    { task: "X/Twitter Short Tweet", points: 10, frequency: "1 per week" },
    { task: "Reddit Post", points: 15, frequency: "1 per week" }
  ],
  visual: [
    { task: "X/Twitter Infographic", points: 15, frequency: "1 per week" },
    { task: "X/Twitter Meme", points: 10, frequency: "1 per week" },
    { task: "Instagram Carousel", points: 15, frequency: "1 per week" },
    { task: "Instagram Post", points: 10, frequency: "1 per week" }
  ],
  video: [
    { task: "YouTube Video", points: 50, frequency: "1 per week" },
    { task: "YouTube Short", points: 25, frequency: "1 per week" },
    { task: "TikTok Video", points: 25, frequency: "1 per week" },
    { task: "Instagram Reel", points: 25, frequency: "1 per week" }
  ]
};

const contentRequirements = [
  "Include a link to rentfi.io and #RentFi #PassiveIncome hashtags for Medium, Reddit, Instagram, YouTube and TikTok tasks",
  "Tag/Mention @rentfi_io and #RentFi #PassiveIncome hashtags for X/Twitter tasks",
  "Clear explanation of RentFi's value proposition",
  "All content must be original and unique",
  "Content must be in English, Spanish, Brazilian Portuguese or French"
];

const keyTopics = [
  "How RentFi democratizes real estate investment",
  "The RENT token utility and mechanics",
  "Our 50/50 mechanism (distribution & burn)",
  "Focus on our unique selling points",
  "Highlight our average yearly yield target (8–20%)",
  "No minimum investment requirement",
  "Professional property management",
  "Global portfolio diversification"
];

const faqs = [
  {
    question: "What type of content should I create?",
    answer: "Focus on educational content about real estate investment, blockchain technology, and RentFi's unique features. Content should be informative, engaging, and aligned with our brand guidelines."
  },
  {
    question: "How are rewards paid out?",
    answer: "Rewards are paid monthly in $RENT to your provided wallet address, typically within the first week of the following month after performance review."
  },
  {
    question: "Can I create content in languages other than English?",
    answer: "Yes! We accept content in English, Spanish, Brazilian Portuguese, and French. Just ensure you maintain consistent quality and messaging."
  },
  {
    question: "How are points calculated?",
    answer: "Points are awarded based on content quality, engagement metrics, and adherence to guidelines. Each content piece is manually reviewed by our team. You can submit one piece per content type per week."
  }
];

function Ambassador() {
  return (
    <>
      <Helmet>
        <title>RentFi | Ambassador Program</title>
        <meta 
          name="description" 
          content="Join RentFi's Ambassador Program and earn rewards by creating content about real estate investment and blockchain technology. Start your journey from Apprentice to Real Estate Developer." 
        />
        <meta 
          name="keywords" 
          content="RentFi ambassador, content creator, real estate content, blockchain ambassador, property investment, creator rewards" 
        />
        <link rel="canonical" href="https://rentfi.io/ambassador" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header Section */}
            <div className="text-center mb-16">
              <H1 className="text-primary mb-4">RentFi Ambassador Program</H1>
              <BodyLarge className="text-gray-600 max-w-3xl mx-auto">
                Create content, earn rewards, and help revolutionize real estate investment through blockchain technology
              </BodyLarge>
            </div>

            {/* Getting Started Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <H2 className="text-primary mb-8 text-center">Getting Started</H2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold">1</span>
                    <H3 className="text-gray-900 ml-3">Apply</H3>
                  </div>
                  <Body className="text-gray-600">
                    Submit your application with examples of your content creation experience
                  </Body>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold">2</span>
                    <H3 className="text-gray-900 ml-3">Create</H3>
                  </div>
                  <Body className="text-gray-600">
                    Start as an Apprentice and create quality content about RentFi
                  </Body>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center mb-4">
                    <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold">3</span>
                    <H3 className="text-gray-900 ml-3">Earn</H3>
                  </div>
                  <Body className="text-gray-600">
                    Earn points, advance through ranks, and receive monthly rewards
                  </Body>
                </div>
              </div>
              <div className="mt-8 text-center">
                <a
                  href="https://tally.so/r/w4zXRX"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-cta inline-flex items-center group"
                  aria-label="Apply to become a RentFi Ambassador"
                >
                  Apply Now
                  <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>

            {/* Ranks and Rewards Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <H2 className="text-primary mb-8 text-center">Ranks & Rewards</H2>
              
              {/* Apprentice Level - Full Width */}
              <div className="mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="text-secondary font-semibold mb-2">Starting Level</div>
                  <H3 className="text-gray-900 mb-2">Apprentice</H3>
                  <div className="text-primary text-2xl font-bold mb-4">Unpaid <span className="text-sm text-gray-600">training period</span></div>
                  <Body className="text-gray-600 mb-4">Learn and prove your content creation skills while building your portfolio. This is your first step towards becoming a paid RentFi Ambassador.</Body>
                  <div className="text-sm text-gray-500">Required: Must reach 75+ points to advance to paid ranks</div>
                </div>
              </div>

              {/* Paid Ranks - Grid Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {ranks.slice(1).map((rank, index) => (
                  <div key={index} className="bg-gray-50 p-6 rounded-lg">
                    <div className="text-secondary font-semibold mb-2">{rank.level}</div>
                    <H3 className="text-gray-900 mb-2">{rank.title}</H3>
                    <div className="text-primary text-2xl font-bold mb-4">{rank.reward} <span className="text-sm text-gray-600">monthly</span></div>
                    <Body className="text-gray-600 mb-4">{rank.description}</Body>
                    <div className="text-sm text-gray-500">Required: {rank.requirements}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Task Points Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <H2 className="text-primary mb-8 text-center">Task Point Values</H2>
              
              {/* Writing Content */}
              <div className="mb-8">
                <H3 className="text-gray-900 mb-4">Writing Content</H3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {taskPoints.writing.map((task, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                      <span className="text-gray-900">{task.task}</span>
                      <div className="flex items-center">
                        <span className="text-primary font-bold mr-2">{task.points} XP</span>
                        <span className="text-sm text-gray-500">{task.frequency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Visual Content */}
              <div className="mb-8">
                <H3 className="text-gray-900 mb-4">Visual Content</H3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {taskPoints.visual.map((task, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                      <span className="text-gray-900">{task.task}</span>
                      <div className="flex items-center">
                        <span className="text-primary font-bold mr-2">{task.points} XP</span>
                        <span className="text-sm text-gray-500">{task.frequency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Video Content */}
              <div className="mb-8">
                <H3 className="text-gray-900 mb-4">Video Content</H3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {taskPoints.video.map((task, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                      <span className="text-gray-900">{task.task}</span>
                      <div className="flex items-center">
                        <span className="text-primary font-bold mr-2">{task.points} XP</span>
                        <span className="text-sm text-gray-500">{task.frequency}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Body className="text-gray-600 mt-8 text-center">
                You can submit one piece per content type per week. Approximately 6-9 quality content pieces per month are needed to reach the 150-point threshold.
                All submissions are manually reviewed for quality assurance.
              </Body>
            </div>

            {/* Content Requirements Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <H2 className="text-primary mb-8 text-center">Content Requirements</H2>
              <div className="space-y-4">
                {contentRequirements.map((requirement, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">{requirement}</Body>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Key Topics Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <H2 className="text-primary mb-8 text-center">Key Topics</H2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {keyTopics.map((topic, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">{topic}</Body>
                    </div>
                  </div>
                ))}
              </div>
              <Body className="text-gray-600 mt-8 text-center italic">
                These topics serve as guidelines and ideas. Feel free to explore other relevant aspects of RentFi and real estate investment in your content.
              </Body>
            </div>

            {/* Monthly Evaluation Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <H2 className="text-primary mb-8 text-center">Monthly Evaluation & Payment Rules</H2>
              
              {/* Core Requirements Box */}
              <div className="bg-secondary/10 p-6 rounded-lg mb-8">
                <H3 className="text-gray-900 mb-4">Core Requirements:</H3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-secondary rounded-full mt-2 mr-3 shrink-0"></span>
                    <Body className="text-gray-900 font-semibold">
                      Apprentice to Property Scout: Minimum 75 points required
                    </Body>
                  </div>
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-secondary rounded-full mt-2 mr-3 shrink-0"></span>
                    <Body className="text-gray-900 font-semibold">
                      All paid ranks (Property Scout and above): Minimum 150 points required for payment
                    </Body>
                  </div>
                  <div className="flex items-start">
                    <span className="w-2 h-2 bg-secondary rounded-full mt-2 mr-3 shrink-0"></span>
                    <Body className="text-gray-900 font-semibold">
                      Rank advancement: Must achieve 150+ points AND be in top 10 performers
                    </Body>
                  </div>
                </div>
              </div>

              {/* Detailed Rules */}
              <div className="space-y-6">
                {/* Apprentice Level */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <H3 className="text-gray-900 mb-4">Apprentice Level Rules:</H3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">
                        Must achieve 75+ points to be promoted to Property Scout
                      </Body>
                    </div>
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">
                        No payment during apprenticeship period
                      </Body>
                    </div>
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">
                        Failing to reach 75 points results in removal from program
                      </Body>
                    </div>
                  </div>
                </div>

                {/* Property Scout Level */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <H3 className="text-gray-900 mb-4">Property Scout Level Rules:</H3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">
                        Must achieve 150+ points to receive monthly payment
                      </Body>
                    </div>
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">
                        Top 10 performers with 150+ points advance to Home Advisor
                      </Body>
                    </div>
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">
                        Failing to reach 150 points results in removal from program
                      </Body>
                    </div>
                  </div>
                </div>

                {/* Higher Ranks */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <H3 className="text-gray-900 mb-4">Higher Ranks Rules (Home Advisor and above):</H3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">
                        Must achieve 150+ points to receive monthly payment
                      </Body>
                    </div>
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">
                        Top 10 performers with 150+ points advance to next rank
                      </Body>
                    </div>
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <Body className="text-gray-600 ml-3">
                        Failing to reach 150 points results in demotion to previous rank
                      </Body>
                    </div>
                  </div>
                </div>

                {/* Payment Summary Box */}
                <div className="bg-primary/10 p-6 rounded-lg">
                  <H3 className="text-gray-900 mb-4">Payment Summary:</H3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 shrink-0"></span>
                      <Body className="text-gray-900">
                        <span className="font-semibold">Apprentice Level:</span> Unpaid training period
                      </Body>
                    </div>
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 shrink-0"></span>
                      <Body className="text-gray-900">
                        <span className="font-semibold">Property Scout and Above:</span> Must achieve minimum 150 points to receive monthly payment
                      </Body>
                    </div>
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 shrink-0"></span>
                      <Body className="text-gray-900">
                        <span className="font-semibold">Rank Advancement:</span> Requires both 150+ points AND top 10 performance
                      </Body>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Join Now CTA Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <div className="text-center">
                <H2 className="text-primary mb-4">Ready to Join Our Community?</H2>
                <BodyLarge className="text-gray-600 mb-8 max-w-2xl mx-auto">
                  Start your journey as a RentFi Ambassador today. Create engaging content, earn rewards, and help revolutionize real estate investment.
                </BodyLarge>
                <a
                  href="https://tally.so/r/w4zXRX"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-cta inline-flex items-center group text-lg px-8 py-4"
                  aria-label="Apply to become a RentFi Ambassador"
                >
                  Apply Now
                  <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <H2 className="text-primary mb-8 text-center">Frequently Asked Questions</H2>
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-start">
                      <QuestionMarkCircleIcon className="w-6 h-6 text-secondary mt-1 shrink-0" />
                      <div className="ml-4">
                        <H3 className="text-gray-900 mb-2">{faq.question}</H3>
                        <Body className="text-gray-600">{faq.answer}</Body>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default Ambassador;